import { Skeleton, GridSkeleton, StatCardsSkeletonGrid } from "@speakai/ui/skeleton";

export type PageContentSkeletonVariant = "form" | "table" | "grid" | "stats";

interface PageContentSkeletonProps {
  /** Content shape. form = stacked cards w/ fields; table = row list; grid = card grid; stats = stat cards + table. */
  variant?: PageContentSkeletonVariant;
  /** Render the title/description header placeholder. Default true. */
  header?: boolean;
  /** form: number of card sections (default 3). table: number of rows (default 6). */
  count?: number;
  /** grid only */
  columns?: 2 | 3 | 4;
  /** grid only */
  rows?: number;
}

/** Page loading skeleton that mirrors the page's real layout. Replaces the generic PageSkeleton. */
export function PageContentSkeleton({ variant = "form", header = true, count, columns = 3, rows = 2 }: PageContentSkeletonProps) {
  return (
    <div className="flex flex-col gap-6" aria-hidden="true">
      {header && (
        <div className="space-y-2">
          <Skeleton className="h-7 w-44" />
          <Skeleton className="h-4 w-64" />
        </div>
      )}
      {variant === "form" &&
        Array.from({ length: count ?? 3 }).map((_, i) => (
          <div key={i} className="rounded-lg border border-border bg-card p-6">
            <Skeleton className="h-5 w-40 mb-4" />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </div>
          </div>
        ))}
      {variant === "table" && (
        <div className="rounded-lg border border-border bg-card overflow-hidden divide-y divide-border">
          {Array.from({ length: count ?? 6 }).map((_, i) => (
            <div key={i} className="h-14 animate-pulse bg-muted/30" />
          ))}
        </div>
      )}
      {variant === "grid" && <GridSkeleton columns={columns} rows={rows} />}
      {variant === "stats" && (
        <>
          <StatCardsSkeletonGrid count={4} />
          <div className="rounded-lg border border-border bg-card overflow-hidden divide-y divide-border">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="h-14 animate-pulse bg-muted/30" />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
